/**
 * dealLink.ts — the deal page's address (deal detail slice 9, §39.9 order 9,
 * R9): `/sales/pipeline/:dealId`, read on load and written by the deal page's
 * own state (Dashboard's pushState / replaceState + popstate). Nothing else in
 * the app has an address: there is no router, and this does not add one.
 *
 * The address survives a sign-in (ruled 2026-10-17): the WorkOS callback always
 * lands on "/", so signIn() stashes a deal's address in sessionStorage and
 * initAuth() puts it back once the session is restored.
 */

const PREFIX = '/sales/pipeline/'
const DEAL_PATH = /^\/sales\/pipeline\/([^/]+)\/?$/
// The server keys deals by uuid and answers a malformed one with a 500 (its
// uuid cast), never a 404 — so the page refuses it here and never sends it.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The address of one deal. */
export function dealPath(id: string): string {
  return PREFIX + encodeURIComponent(id)
}

/** The id an address names, or null when it is not a deal's address. A
 *  segment that does not decode is returned as given — it can only be
 *  malformed, which isDealId then says. */
export function dealIdFromPath(pathname: string): string | null {
  const m = DEAL_PATH.exec(pathname)
  if (!m) return null
  try { return decodeURIComponent(m[1]) } catch { return m[1] }
}

/** Whether an id can name a deal at all (a uuid). */
export function isDealId(id: string): boolean {
  return UUID.test(id)
}

// The sign-in round trip. sessionStorage is this tab's only, and the stash is
// one-shot and short-lived, so an abandoned sign-in cannot reopen a deal later.
const RETURN_KEY = 'klaxiora.dealReturn'
const RETURN_TTL_MS = 10 * 60 * 1000

/** Before the sign-in redirect: remember the address if it names a deal. Any
 *  other address stores nothing (and clears an older stash). */
export function stashDealReturn(pathname: string, now = Date.now()): void {
  try {
    const id = dealIdFromPath(pathname)
    if (id !== null && isDealId(id)) sessionStorage.setItem(RETURN_KEY, JSON.stringify({ id, at: now }))
    else sessionStorage.removeItem(RETURN_KEY)
  } catch { /* storage blocked — the link lands on "/" as before */ }
}

/** After the session is restored: take the stash (once) and, when the page
 *  landed on "/", put the deal's address back before the app reads it. The
 *  address is rebuilt from a re-validated id — the stored text is never used
 *  as a URL, so the stash cannot send the page anywhere but a deal. */
export function restoreDealReturn(now = Date.now()): void {
  let raw: string | null
  try {
    raw = sessionStorage.getItem(RETURN_KEY)
    sessionStorage.removeItem(RETURN_KEY)
  } catch { return }
  if (raw === null || window.location.pathname !== '/') return
  try {
    const { id, at } = JSON.parse(raw) as { id?: unknown; at?: unknown }
    if (typeof id !== 'string' || !isDealId(id)) return
    if (typeof at !== 'number' || now - at < 0 || now - at > RETURN_TTL_MS) return
    window.history.replaceState(null, '', dealPath(id))
  } catch { /* unreadable stash — ignore it */ }
}
